import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { STORAGE_KEYS } from '@/shared/lib/storage'

/**
 * An order that has been completed and is awaiting a team member initials capture.
 */
export interface PendingInitialsEntry {
  /** Representative order id — used for dedupe, display key, and modal identity. */
  orderId: string
  /**
   * Every order the captured initials apply to. For a grouped (partner) order this
   * is all members that were completed together; for a single order it is just
   * `[orderId]`. The initials are written to all of these on submit.
   */
  orderIds: string[]
  customerName?: string
  createdAt: number
}

interface TmInitialsState {
  /**
   * FIFO queue of orders awaiting initials capture. Persisted to localStorage so
   * that an unsubmitted capture survives a reload / reopen and is shown again.
   */
  pending: PendingInitialsEntry[]
}

interface TmInitialsStore extends TmInitialsState {
  /** Queue an order (and its group members) for initials capture (deduped by orderId). */
  enqueue: (entry: { orderId: string, orderIds?: string[], customerName?: string }) => void
  /**
   * Mark an order's initials as captured. This only removes the pending entry:
   * once submitted we have no further use for the value locally (a backend
   * `tm_initials` write is handled separately), so nothing is retained and the
   * persisted queue cannot grow without bound. No-op if the value is blank.
   */
  submit: (orderId: string, initials: string) => void
  /** Remove all pending entries (test / logout hygiene). */
  clearAll: () => void
}

export const useTmInitialsStore = create<TmInitialsStore>()(
  persist(
    set => ({
      pending: [],

      enqueue: ({ orderId, orderIds, customerName }) => set((state) => {
        if (state.pending.some(entry => entry.orderId === orderId))
          return state

        // Always apply to the primary order at minimum; dedupe member ids.
        const applyTo = Array.from(new Set([orderId, ...(orderIds ?? [])]))

        return {
          pending: [...state.pending, { orderId, orderIds: applyTo, customerName, createdAt: Date.now() }],
        }
      }),

      submit: (orderId, initials) => set((state) => {
        if (!initials.trim())
          return state

        return {
          pending: state.pending.filter(entry => entry.orderId !== orderId),
        }
      }),

      clearAll: () => set({ pending: [] }),
    }),
    {
      name: STORAGE_KEYS.TM_INITIALS,
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

/**
 * Selector for the order currently awaiting initials (head of the queue),
 * or null when nothing is pending.
 */
export function useCurrentPendingInitials(): PendingInitialsEntry | null {
  return useTmInitialsStore(state => state.pending[0] ?? null)
}
